import { Component, forwardRef, HostBinding, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ModeView } from '../../../shared/ontology/model/mode-view.enum';
import { isEditableByMode } from '../utils/ontology-editing.util';

export interface RangeValue {
    min?: number | null;
    max?: number | null;
}

@Component({
    selector: 'jhi-numeric-range',
    template: `
		@if (isEditable()) {
			<div class="d-flex gap-2">
				<input
					type="number"
					class="form-control"
					[disabled]="disabled"
					[value]="value?.min ?? ''"
					(input)="onMinInput($event)"
					(blur)="markTouched()"
					[attr.placeholder]="minPlaceholder || null"
				/>

				<input
					type="number"
					class="form-control"
					[disabled]="disabled"
					[value]="value?.max ?? ''"
					(input)="onMaxInput($event)"
					(blur)="markTouched()"
					[attr.placeholder]="maxPlaceholder || null"
				/>
			</div>
		} @else {
			<div class="form-control-plaintext">
				<truncate-span [textInput]=formatRange(value)></truncate-span>
			</div>
		}
    `,
    styles: [
        `
            /* In CREATE modeView, show invalid immediately (even before touch). */
            :host.modeView-create.ng-invalid input.form-control {
                border-left: 6px solid var(--bs-danger);
                padding-left: calc(.75rem - 6px);
            }

            /* In other modes, show invalid only after touch. */
            :host.ng-invalid.ng-touched input.form-control {
                border-left: 6px solid var(--bs-danger);
                padding-left: calc(.75rem - 6px);
            }

            :host.ng-valid.ng-touched input.form-control {
                border-left: 6px solid var(--bs-success);
                padding-left: calc(.75rem - 6px);
            }
        `
    ],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => NumericRangeComponent), multi: true },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => NumericRangeComponent), multi: true }
    ]
})
export class NumericRangeComponent implements ControlValueAccessor, Validator, OnChanges {

    @Input() modeView: ModeView = ModeView.VIEW;

    /** Used only for metadata-based permission checks (EDIT). */
    @Input() model?: any;

    /** Field name checked against metadata.editableFields. */
    @Input() property?: string;

    /** Extra guard: caller can force-disable. */
    @Input() disabled = false;

    /** If the widget applies (your old `om-numeric`). */
    @Input() numeric = true;

    /** Allowed min/max bounds (your old om-min/om-max). */
    @Input() min?: number | null;
    @Input() max?: number | null;

    /** Optional placeholders */
    @Input() minPlaceholder?: string;
    @Input() maxPlaceholder?: string;

    value: RangeValue = {};

    private onChange: (v: RangeValue | null) => void = () => {
    };
    private onTouched: () => void = () => {
    };

    constructor(public translateService: TranslateService) {
    }

    @HostBinding('class.modeView-create')
    public get isCreateMode(): boolean {
        return this.modeView === ModeView.CREATE;
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes['numeric']) {
            if (this.numeric === false) {
                this.value = {};
                this.onChange(this.value);
            }
        }

    }

    writeValue(v: RangeValue | null): void {
        this.value = v ?? {};
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public markTouched(): void {
        this.onTouched();
    }

    public onMinInput(event: Event): void {
        const target = event.target as HTMLInputElement | null;

        if (!target) {
            return;
        }

        const raw = target.value;
        const parsed = raw === '' ? null : Number(raw);

        this.value = { ...(this.value ?? {}), min: parsed };
        this.onChange(this.value);

    }

    public onMaxInput(event: Event): void {
        const target = event.target as HTMLInputElement | null;

        if (!target) {
            return;
        }

        const raw = target.value;
        const parsed = raw === '' ? null : Number(raw);

        this.value = { ...(this.value ?? {}), max: parsed };
        this.onChange(this.value);

    }

    public isEditable(): boolean {
        return isEditableByMode({
            modeView: this.modeView,
            model: this.model,
            property: this.property ?? ''
        });
    }

    public validate(_: AbstractControl): ValidationErrors | null {

        if (this.modeView === ModeView.VIEW) {
            return null;
        }

        if (this.numeric === false) {
            return null;
        }

        const minV = this.value?.min;
        const maxV = this.value?.max;

        const minSpecified = typeof minV === 'number' && !Number.isNaN(minV);
        const maxSpecified = typeof maxV === 'number' && !Number.isNaN(maxV);

        const errors: ValidationErrors = {};

        const validMinProvided = typeof this.min === 'number' && !Number.isNaN(this.min);
        const validMaxProvided = typeof this.max === 'number' && !Number.isNaN(this.max);

        if (minSpecified) {

            if ((validMinProvided && (minV as number) < (this.min as number)) ||
                (validMaxProvided && (minV as number) > (this.max as number))) {
                errors['minOutOfRange'] = true;
            }

            if (maxSpecified && (maxV as number) <= (minV as number)) {
                errors['minTooBig'] = true;
            }

        }

        if (maxSpecified) {

            if ((validMinProvided && (maxV as number) < (this.min as number)) ||
                (validMaxProvided && (maxV as number) > (this.max as number))) {
                errors['maxOutOfRange'] = true;
            }

        }

        return Object.keys(errors).length ? errors : null;
    }

    public formatRange(v: RangeValue | null | undefined): string {

        const minV = v?.min;
        const maxV = v?.max;

        const hasMin = typeof minV === 'number' && !Number.isNaN(minV);
        const hasMax = typeof maxV === 'number' && !Number.isNaN(maxV);

        if (!hasMin && !hasMax) {
            return this.translateService.instant('ontology-manager.tab.scales.modal.scale.numeric.noRange');
        }

        if (hasMin && hasMax) {
            return this.translateService.instant(
                'ontology-manager.tab.scales.modal.scale.numeric.full.range',
                { min: minV, max: maxV }
            );
        }

        if (hasMin) {
            return this.translateService.instant(
                'ontology-manager.tab.scales.modal.scale.numeric.minOnly',
                { min: minV }
            );
        }

        return this.translateService.instant(
            'ontology-manager.tab.scales.modal.scale.numeric.maxOnly',
            { max: maxV }
        );
    }

}
